import { ReactNode, Suspense, lazy, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Layout, Navbar, SubHeader } from "src/components";
import CraHomeScreen from "src/features/fields/pages/Homescreen";
import Loader from "./components/ui/loader.component";
import { isProd, logout } from "./constants/global-const";
import { accessControlActions } from "./context/access-control-slice";
import { authActions } from "./context/auth-slice";
import { RootState } from "./context/store";
import Login from "./pages/auth/login.component";
import BlockedRevoked from "./pages/blocked-counter";
import NotFound from "./pages/not-found/not-found.component";
import StockistDetails from "./pages/stockist-details";
import StockistMaster from "./pages/stockist-master/stockist-master.component";
import { fineGrainPermission } from "src/features/fields/services/openfga";
import { queryEmployeeDetails } from "./services/employee-api";
import CompanyMasterComponent from "./pages/company-master/company-master.component";
import RestrictedPage from "./container/restricted.component";
import OrdersHomePage from "./features/fields/pages/OrdersHomeScreen";
import OrdersDetailScreen from "./features/fields/pages/OrderDetailsTab/OrdersDetailScreen";
import OrdersProductScreen from "./features/fields/pages/OrdersDetail/OrdersProductScreen";
import OrderReviewScreen from "./features/fields/pages/OrdersDetail/OrderReviewScreen";
import OrderConfirmation from "./features/fields/pages/OrdersDetail/OrderConfirmation";
import InvoiceMasterComponent from "./pages/invoice-master/invoice-master.component";
import InvoiceHomepage from "./pages/invoice-homepage/Invoice-homepage";
import { InvoiceEntryScreen } from "./pages/invoice-details/InvoiceEntryScreen";
import OrderInfoScreen from "./features/fields/pages/OrderInfoTabs/OrderInfoScreen";
import { InvoiceApprovalScreen } from "./pages/invoice-details/InvoiceApprovalScreen";
import InvoiceComparison from "./pages/invoice-details/invoice-comparison.component";
import EInvoiceDetails from "./features/fields/pages/E-Invoices/E-InvoiceCards.tsx/E-InvoiceDetails";
import PoHomeScreen from "./features/fields/pages/PurchaseOrder/PoHomeScreen";
import PoBulk from "./features/fields/pages/PurchaseOrder/PoBulk";
import PoItems from "./features/fields/pages/PurchaseOrder/PoItems";
import OrderProductsReport from "./features/fields/pages/Reports/OrderProductsReport";
import ReconciliationHomeScreen from "./features/fields/pages/Reconciliation";
import FeedbackReport from "./features/fields/pages/Reports/FeedbackReport";
import SettlementsHomePage from "./features/fields/pages/Reports/Settlements";

import { InvoiceReEntryScreen } from "./pages/invoice-details/InvoiceReEntryScreen";  // M10-12366
import PoOrdersProductScreen from "./features/fields/pages/OrdersDetail/PoOrderProductScreent";//M10-12866
import OrderPOConfirmation from "./features/fields/pages/OrdersDetail/OrderPOConfirmation";//M10-12866
import { useAuthBootstrap } from "./hooks/useAuthBootstrap";
const CounterDetails = lazy(() => import("./pages/counter-details/counter-details.component"));
const DivisionDetails = lazy(() => import("./pages/division-details/division-details.component"));
const DivisionMaster = lazy(() => import("./pages/division-master/division-master.component"));
const ValueCap = lazy(() => import("./pages/value-cap"));

const EmployeeMaster = lazy(() => import("src/pages/employee-master/employee-master.component"));
const EmployeeDetails = lazy(() => import("src/pages/employees-details"));
const ProductDetails = lazy(() => import("src/pages/product-details"));
const ProductMaster = lazy(() => import("src/pages/product-master/product-master.component"));
const CounterMaster = lazy(() => import("src/pages/counter-master/counter-master.component"));
// const CraHomescreen = lazy(() => import("src/features/fields/pages/Homescreen"));
const PDAHomescreen = lazy(() => import("src/features/fields/pages/PDAHomescreen"));
const CraDetailScreen = lazy(() => import("src/features/fields/pages/DetailScreen"));
const PdaDetailScreen = lazy(() => import("src/features/fields/pages/productDetailsScreen"));
const GeographicMaster = lazy(() => import("src/pages/geography-master/geographic-master.component"));
const FeedbackComponent = lazy(() => import("src/pages/feedback/feedback-component"));
//M10-13117
const MasterReport = lazy(() => import("src/features/fields/pages/Reports/MasterReport"));
const OperationalMetricsReport = lazy(() => import("src/features/fields/pages/Reports/OperationalMetrics"));
const RequestApprovalReport = lazy(() => import("src/features/fields/pages/Reports/RequestApprovalReport"));
const TransactionReport = lazy(() => import("src/features/fields/pages/Reports/TransactionReport"))
//M10-13117

/**
 * Passthrough wrapper. Previously refreshed the access token on every
 * pathname change via `useRefreshOnNavigation`, but that produced a network
 * call on every route click. Refresh now happens reactively from the
 * axios response interceptor on 401/403 — no need to pre-refresh.
 */
function RouteRefreshGate({ children }: { children: ReactNode }): JSX.Element {
  return <>{children}</>;
}

export default function App(): JSX.Element {
  const [isLoading, setIsLoading] = useState(false);

  // Try to restore a session from the backend's httpOnly refresh cookie
  // before showing <Login />. Returns true while the refresh is in flight.
  const bootstrapping = useAuthBootstrap();

  const token: any = useSelector((state: RootState) => state.auth);
  const { idTokenDetails } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();

  useEffect(() => {
    if (idTokenDetails && !token.restricted) { //M10-15057
      getMenuAccessRoles();
    }
  }, [idTokenDetails, token.restricted]); //M10-15057

  const getMenuAccessRoles = async () => {
    try {
      const mv_role = idTokenDetails?.["custom:mv_role"]?.startsWith("L") ? "FieldForce" : idTokenDetails?.["custom:mv_role"];

        // remove special characters and spacing from mv_role
        const cleanedMvRole = mv_role.replace(/[^a-zA-Z0-9]/g, "");

        const tupleKey = {
          user: `staff_position:${cleanedMvRole}`,
          object: "menu:",
        };
        setIsLoading(true)
      const menuAccessRoles = await fineGrainPermission(tupleKey);
      dispatch(accessControlActions.fetchAccessControl(menuAccessRoles?.data?.tuples ?? []));
      setIsLoading(false);
    } catch (error) {
      console.error("Unable to fetch Menu Access Control Roles:", error);
    }
  };

  useEffect(() => {
    // const token: any = {
    //   token: {
    //     // sub: "55fc3d2f-f6c1-49f3-8b91-1ee91d60a971",
    //     sub: "729c1fcd-7285-48f8-91c8-1352fdad555f",
    //     "custom:role": "fieldforce",
    //   },
    // };

    if (token?.token && token?.token?.sub && !token.restricted) { //M10-15057
      // const isAdmin = token?.token?.["custom:role"] === "admin" || token?.token?.["custom:role"] === "Medvol Field";
      const isAdmin = true;
      const query = isAdmin ? `/details?cognito_id=${token?.token?.sub}&cdh=true` : `/details?cognito_id=${token?.token?.sub}`;
      queryEmployeeDetails(query)
        .then((res) => {
          if (isAdmin) {
            dispatch(authActions.setCurrentPsr(res?.data?.[0] || []));
          } else {
            dispatch(authActions.setCurrentPsr(res?.data?.data?.[0] || []));
          }
        })
        .catch((err) => console.log(err));
    }
  }, [token?.token, token.restricted]);//M10-15057

  // useEffect(() => {
  //   const idTokenDetails = {
  //     "custom:role": "admin",
  //   };
  //   dispatch(authActions.setIdTokenDetails(idTokenDetails));
  // }, []);
  // logout after
  useEffect(() => {
    const currentTime = 120 * 60 * 1000; // 120 minutes = 2 hours
    // const currentTime = 1 * 60 * 1000; // 1 minutes

    const events = ["click", "load", "keypress"];
    let timer = setTimeout(() => logout(false), currentTime);

    const resetTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(() => logout(false), currentTime);
    };

    events.forEach((event) => window.addEventListener(event, resetTimer));

    return () => {
      events.forEach((event) => window.removeEventListener(event, resetTimer));
      clearTimeout(timer);
    };
  }, []);

  // listen for logout in different tabs
  useEffect(() => {
    const channel = new BroadcastChannel("logout");
    channel.onmessage = () => {
      console.log("logout");
      logout(false, false);
    };

    return () => channel.close();
  }, []);

  if (isLoading || bootstrapping) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader />
      </div>
    );
  }

  // useEffect(() => {
  //   // const getCookie = await getCookieValue("token");
  //   const setDataInRedux = async () => {
  //     const getIdToken = await getCookieValue("idToken");

  //     console.log("getCookie", getIdToken);

  //     if (getIdToken) {
  //       const decodedToken: string | null | undefined = await jwtDecode(getIdToken as string);
  //       let decodedName: any = null;
  //       if (getIdToken) {
  //         decodedName = await jwtDecode(getIdToken as string);
  //       }
  //       dispatch(authActions.login(decodedToken));
  //       dispatch(authActions.setLoggedInUser(decodedName?.name || ""));
  //       console.log("it is logged in");
  //     }
  //   };

  //   setDataInRedux();
  // }, [getCookieValue("idToken")]);

  return (
    <>
      {token.restricted && <RestrictedPage />}
      {!token.token && <Login />}
      {token.token && (!token.restricted) && (
        <Suspense fallback={<Loader />}>
          <BrowserRouter>
            <RouteRefreshGate>
            <Routes>
              <Route path="/" element={<Navigate to="masters" />} />
              <Route
                path="/masters"
                element={
                  <>
                    <Navbar />
                    <SubHeader />
                  </>
                }
              />

              {/* masters route starts here */}
                <>
                  <Route path="/Masters" element={<Layout />}>
                    {/* {products routes} */}
                        <Route path="products" element={<ProductMaster />} />
                        <Route path="products/details" element={<ProductDetails />} />
                    {/* employees routes  */}
                        <Route path="employees" element={<EmployeeMaster />} />
                        <Route path="employees/details" element={<EmployeeDetails />} />
                    {/*counters routes*/}
                        <Route path="counters" element={<CounterMaster />} />
                        <Route path="counters/details" element={<CounterDetails />} />
                        <Route path="counters/details/value-cap" element={<ValueCap />} />
                        <Route path="counters/details/block-revoke" element={<BlockedRevoked />} />
                        <Route path="divisions" element={<DivisionMaster />} />
                        <Route path="divisions/details" element={<DivisionDetails />} />
                    {/* stockists route */}
                        <Route path="stockists" element={<StockistMaster />} />
                        <Route path="stockists/details" element={<StockistDetails />} />
                    <Route path="geographic" element={<GeographicMaster />} />
                    {!isProd && <Route path="company" element={<CompanyMasterComponent />} />}
                    {!isProd && <Route path="invoice" element={<InvoiceMasterComponent />} />}
                  </Route>
                  {/* master route ends here */}
                </>
              {/* request approval routes starts here  */}

              {/* M10-15110 */}
              <Route
                path="/request-approval"
                element={
                  <>
                    <Navbar />
                    <SubHeader />
                  </>
                }
              />
              {/* M10-15110 */}
              <Route path="/request-approval" element={<Layout />}>
                <Route
                  path="cra"
                  element={
                    <>
                      <SubHeader />
                      <CraHomeScreen />
                    </>
                  }
                />
                <Route
                  path="cra/details"
                  element={
                    <>
                      <SubHeader />
                      <CraDetailScreen />
                    </>
                  }
                />

                <Route
                  path="pra/details"
                  element={
                    <>
                      <SubHeader />
                      <PdaDetailScreen />
                    </>
                  }
                />
                <Route
                  path="Pra"
                  element={
                    <>
                      <SubHeader />
                      <PDAHomescreen />
                    </>
                  }
                />
              </Route>
              {/* request approval routes ends here  */}

              {/* transactions routes starts here  */}
              {/* <Route
                path="/transactions"
                element={
                  <>
                    <Navbar />
                    <SubHeader />
                  </>
                }
              > */}

              {/* M10-15110 */}
              <Route
                path="/transactions"
                element={
                  <>
                    <Navbar />
                    <SubHeader />
                  </>
                }
              />
              {/* M10-15110 */}
              <Route path="/transactions" element={<Layout />}>
                <Route
                  path="orders"
                  element={
                    <>
                      <SubHeader />
                      <OrdersHomePage />
                    </>
                  }
                />
                <Route
                  path="orders/:tabIndex"
                  element={
                    <>
                      <SubHeader />
                      <OrdersHomePage />
                    </>
                  }
                />
                <Route
                  path="/transactions/orders/details/:counter_code/:tabIndex"
                  element={
                    <>
                      <SubHeader />
                      <OrdersDetailScreen />
                    </>
                  }
                />
                <Route
                  path="/transactions/orders/info/:orderCode/:tabIndex"
                  element={
                    <>
                      <SubHeader />
                      <OrderInfoScreen />
                    </>
                  }
                />
                <Route
                  path="/transactions/orders/products/:locationCode"
                  element={
                    <>
                      <SubHeader />
                      <OrdersProductScreen />
                    </>
                  }
                />
                {/* M10-12866 */}
                <Route
                  path="/transactions/orders/po/products/:locationCode"
                  element={
                    <>
                      <SubHeader />
                      <PoOrdersProductScreen />
                    </>
                  }
                />
                {/* M10-12866 */}
                <Route
                  path="/transactions/orders/review/:locationCode"
                  element={
                    <>
                      <SubHeader />
                      <OrderReviewScreen />
                    </>
                  }
                />
                <Route
                  path="/transactions/orders/confirmation"
                  element={
                    <>
                      <SubHeader />
                      <OrderConfirmation />
                    </>
                  }
                />
                {/* //M10-12866 */}
                 <Route
                  path="/transactions/po/confirmation"
                  element={
                    <>
                      <SubHeader />
                      <OrderPOConfirmation />
                    </>
                  }
                />
                {/* //M10-12866 */}
                {/* <Route path="orders/home" element={<OrdersHomePage />} /> */}

                {/*Invoices routes start here */}
                <Route
                  path="invoice"
                  element={
                    <>
                      <InvoiceHomepage />
                    </>
                  }
                />
                <Route
                  path="invoice/:tabIndex"
                  element={
                    <>
                      <InvoiceHomepage />
                    </>
                  }
                />
                <Route
                  path="/transactions/invoice/:stockist_invoice_attachment_id/0"
                  element={
                    <>
                      <InvoiceEntryScreen />
                    </>
                  }
                />
                <Route
                  path="/transactions/invoice/:stockist_invoice_attachment_id/1"
                  element={
                    <>
                      <InvoiceApprovalScreen />
                    </>
                  }
                />
                {/*  M10-12366 */}
                <Route
                  path="/transactions/invoice/:stockist_invoice_attachment_id/2"
                  element={
                    <>
                      <InvoiceReEntryScreen />
                    </>
                  }
                />
                {/*  M10-12366 */}
                <Route
                  path="/transactions/invoice/product-mapping/:stockist_invoice_attachment_id"
                  element={
                    <>
                      <InvoiceComparison />
                    </>
                  }
                />
                <Route
                  path="/transactions/e-invoice/:einvoice_dtl_id"
                  element={
                    <>
                      <EInvoiceDetails />
                    </>
                  }
                />
                {/* Purchase Order routes start here */}
                <Route
                  path="/transactions/purchase-order"
                  element={
                    <>
                      <PoHomeScreen />
                    </>
                  }
                />
                <Route
                  path="/transactions/purchase-order/:poBatchId"
                  element={
                    <>
                      <PoBulk />
                    </>
                  }
                />
                <Route
                  path="/transactions/purchase-order/:poBatchId/:po_id"
                  element={
                    <>
                      <PoItems />
                    </>
                  }
                />
                   <Route
                  path="/transactions/reconciliation"
                  element={
                    <>
                      <ReconciliationHomeScreen />
                    </>
                  }
                />
                {/* Purchase Order routes ends here */}
              </Route>
              {/* transactions routes ends here  */}

              {/*Reports routes starts here  */}
              {/* M10-15110 */}
              <Route
                path="/reports"
                element={
                  <>
                    <Navbar />
                    <SubHeader />
                  </>
                }
              />
              {/* M10-15110 */}
              <Route path="/reports" element={<Layout />}>
                {/* <Route
                  path="orders-superset"
                  element={
                    <>
                      <SubHeader />
                      <ReportsHomePage />
                    </>
                  }
                /> */}
                <Route
                  path="/reports/counter"//M10-11806
                  element={
                    <>
                      <SubHeader />
                      <OrderProductsReport />
                    </>
                  }
                />

                <Route
                  path="/reports/feedback"
                  element={
                    <>
                      <SubHeader />
                      <FeedbackReport />
                    </>
                  }
                />

                {/* M10-13117 */}
                <Route
                  path="/reports/master"
                  element={
                    <>
                      <SubHeader />
                      <MasterReport />
                    </>
                  }
                />
                   <Route
                  path="/reports/request-approval"
                  element={
                    <>
                      <SubHeader />
                      <RequestApprovalReport />
                    </>
                  }
                />
                <Route
                  path="/reports/transaction"
                  element={
                    <>
                      <SubHeader />
                      <TransactionReport />
                    </>
                  }
                />
                {/* M10-13117 */}

                <Route
                  path="/reports/operational-metrics"
                  element={
                    <>
                      <SubHeader />
                      <OperationalMetricsReport />
                    </>
                  }
                />

                <Route
                  path="/reports/settlements"
                  element={
                    <>
                      <SubHeader />
                      <SettlementsHomePage />
                    </>
                  }
                />
              </Route>
              
              {/* feedback routes starts here  */}

              <Route path="/feedback" element={<Navigate to="/feedback/operational-feedback" />} />
              <Route path="/feedback" element={<Layout />}>
                <Route
                  path="operational-feedback"
                  element={
                    <>
                      <SubHeader />
                      <FeedbackComponent />
                    </>
                  }
                />
              </Route>
              {/* feedback routes ends here  */}

              <Route path="*" element={<NotFound />} />
            </Routes>
            </RouteRefreshGate>
          </BrowserRouter>
        </Suspense>
      )}
    </>
  );
}
